import { Actor, HttpAgent } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';

// IDL factory for user payment canister based on the actual DID file
const USER_PAYMENT_IDL = ({ IDL }) => {
  const BillingInterval = IDL.Variant({
    'Weekly': IDL.Null,
    'Quarterly': IDL.Null,
    'Daily': IDL.Null,
    'Custom': IDL.Nat64,
    'Monthly': IDL.Null,
    'Yearly': IDL.Null,
  });

  const BrandingConfig = IDL.Record({
    'company_name': IDL.Text,
    'logo_url': IDL.Opt(IDL.Text),
    'terms_url': IDL.Opt(IDL.Text),
    'support_url': IDL.Opt(IDL.Text),
  });

  const CouponType = IDL.Variant({
    'FreeShipping': IDL.Null,
    'FixedAmount': IDL.Nat64,
    'Percentage': IDL.Nat32,
  });

  const DiscountCoupon = IDL.Record({
    'updated_at': IDL.Nat64,
    'usage_limit': IDL.Opt(IDL.Nat32),
    'applicable_tokens': IDL.Vec(IDL.Text),
    'code': IDL.Text,
    'coupon_id': IDL.Text,
    'description': IDL.Text,
    'created_at': IDL.Nat64,
    'minimum_amount': IDL.Opt(IDL.Nat64),
    'coupon_type': CouponType,
    'used_count': IDL.Nat32,
    'is_active': IDL.Bool,
    'expires_at': IDL.Opt(IDL.Nat64),
  });

  const InvoiceStatus = IDL.Variant({
    'Paid': IDL.Null,
    'Cancelled': IDL.Null,
    'Created': IDL.Null,
    'Expired': IDL.Null,
  });

  const ModalAnalytics = IDL.Record({
    'conversion_rate': IDL.Float64,
    'revenue_generated': IDL.Nat64,
    'modal_id': IDL.Text,
    'successful_payments': IDL.Nat64,
    'total_views': IDL.Nat64,
  });

  const ModalTheme = IDL.Record({
    'text_color': IDL.Text,
    'border_radius': IDL.Nat32,
    'font_family': IDL.Text,
    'primary_color': IDL.Text,
    'background_color': IDL.Text,
  });

  const PaymentOptions = IDL.Record({
    'require_shipping': IDL.Bool,
    'enable_tips': IDL.Bool,
    'require_email': IDL.Bool,
    'show_amount_breakdown': IDL.Bool,
    'allowed_tokens': IDL.Vec(IDL.Text),
  });

  const RedirectUrls = IDL.Record({
    'webhook_url': IDL.Opt(IDL.Text),
    'success_url': IDL.Text,
    'cancel_url': IDL.Text,
  });

  const ModalConfig = IDL.Record({
    'payment_options': PaymentOptions,
    'theme': ModalTheme,
    'updated_at': IDL.Nat64,
    'redirect_urls': RedirectUrls,
    'name': IDL.Text,
    'description': IDL.Opt(IDL.Text),
    'created_at': IDL.Nat64,
    'template_id': IDL.Opt(IDL.Text),
    'modal_id': IDL.Text,
    'is_active': IDL.Bool,
    'branding': BrandingConfig,
  });

  const TokenConfig = IDL.Record({
    'fee': IDL.Nat64,
    'decimals': IDL.Nat8,
    'logo': IDL.Opt(IDL.Text),
    'name': IDL.Text,
    'canister_id': IDL.Principal,
    'is_active': IDL.Bool,
    'symbol': IDL.Text,
  });

  const TransactionStatus = IDL.Variant({
    'Failed': IDL.Text,
    'Refunded': IDL.Null,
    'Completed': IDL.Null,
    'Pending': IDL.Null,
  });

  const UserCanisterConfig = IDL.Record({
    'merchant_fee': IDL.Nat32,
    'name': IDL.Text,
    'custom_settings': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'description': IDL.Text,
    'webhook': IDL.Opt(IDL.Text),
    'withdraw_threshold': IDL.Opt(IDL.Nat64),
    'auto_withdraw': IDL.Bool,
    'supported_tokens': IDL.Vec(TokenConfig),
  });

  const PaymentAnalytics = IDL.Record({
    'success_rate': IDL.Float64,
    'total_transactions': IDL.Nat64,
    'top_tokens': IDL.Vec(IDL.Text),
    'total_volume': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat64)),
    'average_amount': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat64)),
  });

  const PaymentInvoice = IDL.Record({
    'id': IDL.Text,
    'status': InvoiceStatus,
    'token': TokenConfig,
    'metadata': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'description': IDL.Text,
    'created_at': IDL.Nat64,
    'merchant': IDL.Principal,
    'amount': IDL.Nat64,
    'expires_at': IDL.Opt(IDL.Nat64),
  });

  const PaymentMethod = IDL.Variant({
    'TransferFrom': IDL.Null,
    'Direct': IDL.Null,
    'Subscription': IDL.Null,
  });

  const PaymentTransaction = IDL.Record({
    'id': IDL.Text,
    'to': IDL.Principal,
    'fee': IDL.Nat64,
    'status': TransactionStatus,
    'merchant_fee': IDL.Nat64,
    'token': TokenConfig,
    'block_index': IDL.Opt(IDL.Nat64),
    'metadata': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'from': IDL.Principal,
    'payment_method': PaymentMethod,
    'timestamp': IDL.Nat64,
    'amount': IDL.Nat64,
  });

  const ProductStatus = IDL.Variant({
    'Inactive': IDL.Null,
    'Active': IDL.Null,
    'OutOfStock': IDL.Null,
  });

  const Product = IDL.Record({
    'status': ProductStatus,
    'updated_at': IDL.Nat64,
    'product_id': IDL.Text,
    'token_symbol': IDL.Text,
    'image_url': IDL.Opt(IDL.Text),
    'inventory_count': IDL.Opt(IDL.Nat32),
    'metadata': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'name': IDL.Text,
    'description': IDL.Text,
    'created_at': IDL.Nat64,
    'category': IDL.Opt(IDL.Text),
    'price': IDL.Nat64,
  });

  const ProductSalesStats = IDL.Record({
    'total_sales': IDL.Nat64,
    'product_id': IDL.Text,
    'last_sale_at': IDL.Opt(IDL.Nat64),
    'total_revenue': IDL.Nat64,
    'units_sold': IDL.Nat32,
  });

  const PaymentRequest = IDL.Record({
    'invoice_id': IDL.Text,
    'token_symbol': IDL.Text,
    'metadata': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'coupon_code': IDL.Opt(IDL.Text),
    'amount': IDL.Nat64,
  });

  const PaymentResult = IDL.Record({
    'transaction_id': IDL.Text,
    'block_index': IDL.Opt(IDL.Nat64),
    'payment_method': PaymentMethod,
    'discount_applied': IDL.Nat64,
    'amount_paid': IDL.Nat64,
    'final_amount': IDL.Nat64,
  });

  const CouponUsage = IDL.Record({
    'user_principal': IDL.Principal,
    'invoice_id': IDL.Text,
    'usage_id': IDL.Text,
    'coupon_id': IDL.Text,
    'used_at': IDL.Nat64,
    'discount_applied': IDL.Nat64,
  });

  const SubscriptionStatus = IDL.Variant({
    'Paused': IDL.Null,
    'PendingPayment': IDL.Null,
    'Active': IDL.Null,
    'Cancelled': IDL.Null,
    'Expired': IDL.Null,
  });

  const SubscriptionPlan = IDL.Record({
    'billing_interval': BillingInterval,
    'updated_at': IDL.Nat64,
    'features': IDL.Vec(IDL.Text),
    'token': IDL.Text,
    'name': IDL.Text,
    'trial_period_days': IDL.Opt(IDL.Nat32),
    'max_subscriptions': IDL.Opt(IDL.Nat32),
    'description': IDL.Text,
    'created_at': IDL.Nat64,
    'plan_id': IDL.Text,
    'is_active': IDL.Bool,
    'price': IDL.Nat64,
  });

  const Subscription = IDL.Record({
    'status': SubscriptionStatus,
    'payment_failures': IDL.Nat32,
    'updated_at': IDL.Nat64,
    'cancelled_at': IDL.Opt(IDL.Nat64),
    'subscription_id': IDL.Text,
    'metadata': IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text)),
    'trial_end': IDL.Opt(IDL.Nat64),
    'next_billing_date': IDL.Nat64,
    'current_period_start': IDL.Nat64,
    'created_at': IDL.Nat64,
    'current_period_end': IDL.Nat64,
    'plan_id': IDL.Text,
    'total_payments': IDL.Nat64,
    'subscriber': IDL.Principal,
    'cancel_at_period_end': IDL.Bool,
  });

  const SubscriptionPayment = IDL.Record({
    'transaction_id': IDL.Opt(IDL.Text),
    'status': IDL.Text,
    'payment_date': IDL.Nat64,
    'token': IDL.Text,
    'failure_reason': IDL.Opt(IDL.Text),
    'subscription_id': IDL.Text,
    'billing_period_start': IDL.Nat64,
    'billing_period_end': IDL.Nat64,
    'payment_id': IDL.Text,
    'amount': IDL.Nat64,
  });

  // Result types matching the DID file
  const Result = IDL.Variant({ 'Ok': IDL.Null, 'Err': IDL.Text });
  const Result_1 = IDL.Variant({ 'Ok': IDL.Nat32, 'Err': IDL.Text });
  const Result_2 = IDL.Variant({ 'Ok': IDL.Text, 'Err': IDL.Text });
  const Result_3 = IDL.Variant({ 'Ok': PaymentInvoice, 'Err': IDL.Text });
  const Result_4 = IDL.Variant({ 'Ok': DiscountCoupon, 'Err': IDL.Text });
  const Result_5 = IDL.Variant({ 'Ok': IDL.Tuple(IDL.Nat32, IDL.Vec(CouponUsage)), 'Err': IDL.Text });
  const Result_6 = IDL.Variant({ 'Ok': ModalAnalytics, 'Err': IDL.Text });
  const Result_7 = IDL.Variant({ 'Ok': ModalConfig, 'Err': IDL.Text });
  const Result_8 = IDL.Variant({ 'Ok': Product, 'Err': IDL.Text });
  const Result_9 = IDL.Variant({ 'Ok': ProductSalesStats, 'Err': IDL.Text });
  const Result_10 = IDL.Variant({ 'Ok': Subscription, 'Err': IDL.Text });
  const Result_11 = IDL.Variant({ 'Ok': SubscriptionPayment, 'Err': IDL.Text });
  const Result_12 = IDL.Variant({ 'Ok': SubscriptionPlan, 'Err': IDL.Text });
  const Result_13 = IDL.Variant({ 'Ok': PaymentTransaction, 'Err': IDL.Text });
  const Result_14 = IDL.Variant({ 'Ok': PaymentResult, 'Err': IDL.Text });
  const Result_15 = IDL.Variant({ 'Ok': IDL.Bool, 'Err': IDL.Text });
  const Result_16 = IDL.Variant({ 'Ok': ProductStatus, 'Err': IDL.Text });
  const Result_17 = IDL.Variant({ 'Ok': IDL.Tuple(IDL.Text, IDL.Nat64), 'Err': IDL.Text });
  const Result_18 = IDL.Variant({ 'Ok': IDL.Nat64, 'Err': IDL.Text });

  return IDL.Service({
    'add_supported_token': IDL.Func([TokenConfig], [Result], []),
    'admin_clear_all_coupons': IDL.Func([], [Result_1], []),
    'admin_clear_all_products': IDL.Func([], [Result_1], []),
    'admin_clear_all_subscriptions': IDL.Func([], [Result_1], []),
    'admin_update_owner': IDL.Func([IDL.Principal], [Result], []),
    'cancel_subscription': IDL.Func([IDL.Text, IDL.Bool], [Result], []),
    'canister_id': IDL.Func([], [IDL.Principal], ['query']),
    'create_coupon': IDL.Func([DiscountCoupon], [Result_2], []),
    'create_invoice': IDL.Func(
      [IDL.Nat64, IDL.Text, IDL.Text, IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text))],
      [Result_3],
      []
    ),
    'create_invoice_for_product': IDL.Func(
      [IDL.Text, IDL.Nat32, IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text))],
      [Result_3],
      []
    ),
    'create_modal_config': IDL.Func([ModalConfig], [Result_2], []),
    'create_product': IDL.Func([Product], [Result_2], []),
    'create_subscription': IDL.Func([IDL.Text, IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text))], [Result_2], []),
    'create_subscription_plan': IDL.Func([SubscriptionPlan], [Result_2], []),
    'delete_coupon': IDL.Func([IDL.Text], [Result], []),
    'delete_modal_config': IDL.Func([IDL.Text], [Result], []),
    'delete_product': IDL.Func([IDL.Text], [Result], []),
    'delete_subscription_plan': IDL.Func([IDL.Text], [Result], []),
    'generate_modal_embed_code': IDL.Func([IDL.Text], [Result_2], ['query']),
    'get_all_balances': IDL.Func([], [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat64))], ['query']),
    'get_analytics': IDL.Func(
      [IDL.Opt(IDL.Text), IDL.Opt(IDL.Text)],
      [PaymentAnalytics],
      ['query']
    ),
    'get_balance': IDL.Func([IDL.Text], [IDL.Nat64], ['query']),
    'get_configuration': IDL.Func([], [UserCanisterConfig], ['query']),
    'get_coupon': IDL.Func([IDL.Text], [Result_4], ['query']),
    'get_coupon_by_code': IDL.Func([IDL.Text], [Result_4], ['query']),
    'get_coupon_usage_stats': IDL.Func([IDL.Text], [Result_5], ['query']),
    'get_enhanced_analytics': IDL.Func([], [PaymentAnalytics], ['query']),
    'get_invoice': IDL.Func([IDL.Text], [IDL.Opt(PaymentInvoice)], ['query']),
    'get_modal_analytics': IDL.Func([IDL.Text], [Result_6], ['query']),
    'get_modal_config': IDL.Func([IDL.Text], [Result_7], ['query']),
    'get_owner': IDL.Func([], [IDL.Principal], ['query']),
    'get_payment_method_analytics': IDL.Func([], [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Nat64))], ['query']),
    'get_product': IDL.Func([IDL.Text], [Result_8], ['query']),
    'get_product_categories': IDL.Func([], [IDL.Vec(IDL.Text)], ['query']),
    'get_product_sales_stats': IDL.Func([IDL.Text], [Result_9], ['query']),
    'get_product_stats': IDL.Func([], [IDL.Tuple(IDL.Nat32, IDL.Nat32)], ['query']),
    'get_subscription': IDL.Func([IDL.Text], [Result_10], ['query']),
    'get_subscription_payment': IDL.Func([IDL.Text], [Result_11], ['query']),
    'get_subscription_plan': IDL.Func([IDL.Text], [Result_12], ['query']),
    'get_subscription_stats': IDL.Func([], [IDL.Tuple(IDL.Nat32, IDL.Nat32, IDL.Nat32)], ['query']),
    'get_supported_tokens': IDL.Func([], [IDL.Vec(TokenConfig)], ['query']),
    'get_transaction': IDL.Func([IDL.Text], [IDL.Opt(PaymentTransaction)], ['query']),
    'get_transaction_history': IDL.Func(
      [IDL.Nat64, IDL.Nat64],
      [IDL.Vec(PaymentTransaction)],
      ['query']
    ),
    'health': IDL.Func([], [IDL.Text, IDL.Nat64, IDL.Nat64], ['query']),
    'list_active_coupons': IDL.Func([], [IDL.Vec(DiscountCoupon)], ['query']),
    'list_active_products': IDL.Func([], [IDL.Vec(Product)], ['query']),
    'list_active_subscription_plans': IDL.Func([], [IDL.Vec(SubscriptionPlan)], ['query']),
    'list_all_product_sales_stats': IDL.Func([], [IDL.Vec(ProductSalesStats)], ['query']),
    'list_all_subscriptions': IDL.Func([], [IDL.Vec(Subscription)], ['query']),
    'list_my_coupons': IDL.Func([], [IDL.Vec(DiscountCoupon)], ['query']),
    'list_my_modals': IDL.Func([], [IDL.Vec(ModalConfig)], ['query']),
    'list_my_subscriptions': IDL.Func([], [IDL.Vec(Subscription)], ['query']),
    'list_products': IDL.Func([], [IDL.Vec(Product)], ['query']),
    'list_products_by_category': IDL.Func([IDL.Text], [IDL.Vec(Product)], ['query']),
    'list_products_by_token': IDL.Func([IDL.Text], [IDL.Vec(Product)], ['query']),
    'list_subscription_payments': IDL.Func([IDL.Text], [IDL.Vec(SubscriptionPayment)], ['query']),
    'list_subscription_plans': IDL.Func([], [IDL.Vec(SubscriptionPlan)], ['query']),
    'list_subscriptions_by_plan': IDL.Func([IDL.Text], [IDL.Vec(Subscription)], ['query']),
    'list_user_subscriptions': IDL.Func([IDL.Principal], [IDL.Vec(Subscription)], ['query']),
    'pause_subscription': IDL.Func([IDL.Text], [Result], []),
    'process_payment': IDL.Func([IDL.Text, IDL.Principal], [Result_13], []),
    'process_payment_request': IDL.Func([PaymentRequest], [Result_14], []),
    'process_subscription_payment': IDL.Func([IDL.Text], [Result_2], []),
    'remove_supported_token': IDL.Func([IDL.Text], [Result], []),
    'resume_subscription': IDL.Func([IDL.Text], [Result], []),
    'toggle_coupon_status': IDL.Func([IDL.Text], [Result_15], []),
    'toggle_product_status': IDL.Func([IDL.Text], [Result_16], []),
    'toggle_subscription_plan_status': IDL.Func([IDL.Text], [Result_15], []),
    'toggle_token_status': IDL.Func([IDL.Text], [Result_15], []),
    'track_modal_view': IDL.Func([IDL.Text], [Result], []),
    'update_configuration': IDL.Func([UserCanisterConfig], [Result], []),
    'update_coupon': IDL.Func([IDL.Text, DiscountCoupon], [Result], []),
    'update_modal_config': IDL.Func([IDL.Text, ModalConfig], [Result], []),
    'update_product': IDL.Func([IDL.Text, Product], [Result], []),
    'update_product_inventory': IDL.Func([IDL.Text, IDL.Opt(IDL.Nat32)], [Result], []),
    'update_subscription_metadata': IDL.Func([IDL.Text, IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text))], [Result], []),
    'update_subscription_plan': IDL.Func([IDL.Text, SubscriptionPlan], [Result], []),
    'update_supported_token': IDL.Func([IDL.Text, TokenConfig], [Result], []),
    'validate_and_use_coupon': IDL.Func([IDL.Text, IDL.Nat64, IDL.Text], [Result_17], []),
    'whoami': IDL.Func([], [IDL.Principal], ['query']),
    'withdraw': IDL.Func([IDL.Text, IDL.Nat64, IDL.Principal], [Result_18], []),
  });
};

// Create actor for user payment canister
export const createUserPaymentActor = (canisterId, options = {}) => {
  const agent = new HttpAgent({
    host: 'https://ic0.app',
    ...options.agentOptions
  });

  // SDK is always for production - no root key fetching needed

  return Actor.createActor(USER_PAYMENT_IDL, {
    agent,
    canisterId: Principal.fromText(canisterId)
  });
};

// User Payment Canister service
export class UserPaymentService {
  constructor(canisterId, identity = null) {
    this.canisterId = canisterId;
    this.actor = null;
    this.identity = identity;
  }

  async initializeActor(identity = null) {
    if (identity) {
      this.identity = identity;
    }

    const agent = new HttpAgent({
      host: 'https://ic0.app',
      identity: this.identity
    });

    if (process.env.NODE_ENV !== 'production') {
      await agent.fetchRootKey();
    }

    this.actor = Actor.createActor(USER_PAYMENT_IDL, {
      agent,
      canisterId: Principal.fromText(this.canisterId)
    });

    return this.actor;
  }

  async getSupportedTokens() {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.get_supported_tokens();
      return result.map(token => ({
        name: token.name,
        canister: token.canister_id.toText(),
        canister_id: token.canister_id.toText(),
        symbol: token.symbol,
        decimals: Number(token.decimals),
        fee: Number(token.fee || 0),
        logo: token.logo?.[0] || null,
        is_active: token.is_active,
        id: token.symbol.toLowerCase()
      }));
    } catch (error) {
      console.error('Failed to get supported tokens from user payment canister:', error);
      
      // Fallback to hardcoded tokens
      return [
        { id: 'icp', name: 'ICP', symbol: 'ICP', canister: 'ryjl3-tyaaa-aaaaa-aaaba-cai', canister_id: 'ryjl3-tyaaa-aaaaa-aaaba-cai', decimals: 8, fee: 10000, is_active: true },
        { id: 'ruggy', name: 'Ruggy', symbol: 'RUGGY', canister: 'icaf7-3aaaa-aaaam-qcx3q-cai', canister_id: 'icaf7-3aaaa-aaaam-qcx3q-cai', decimals: 8, fee: 0, is_active: true },
        { id: 'ratsy', name: 'Ratsy', symbol: 'RATSY', canister: 'bzx5a-iyaaa-aaaaa-qbmvq-cai', canister_id: 'bzx5a-iyaaa-aaaaa-qbmvq-cai', decimals: 8, fee: 0, is_active: true }
      ];
    }
  }

  async getCanisterConfiguration() {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.get_configuration();
      return {
        name: result.name,
        description: result.description,
        supported_tokens: result.supported_tokens.map(token => ({
          name: token.name,
          canister: token.canister_id.toText(),
          canister_id: token.canister_id.toText(),
          symbol: token.symbol,
          decimals: Number(token.decimals),
          fee: Number(token.fee || 0),
          logo: token.logo?.[0] || null,
          is_active: token.is_active,
          id: token.symbol.toLowerCase()
        })),
        webhook: result.webhook?.[0] || null,
        merchant_fee: Number(result.merchant_fee),
        auto_withdraw: result.auto_withdraw,
        withdraw_threshold: result.withdraw_threshold?.[0] ? Number(result.withdraw_threshold[0]) : null,
        custom_settings: result.custom_settings.map(([key, value]) => ({ key, value }))
      };
    } catch (error) {
      console.error('Failed to get canister configuration:', error);
      return null;
    }
  }

  async listModals() {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.list_my_modals();
      console.log('Raw modals from canister:', result);
      return result.map(config => ({
        modal_id: config.modal_id || 'mock-id',
        name: config.name || '',
        description: (config.description && config.description.length > 0) ? config.description[0] : '',
        company_name: config.branding?.company_name || '',
        company_logo: (config.branding?.logo_url && config.branding.logo_url.length > 0) ? config.branding.logo_url[0] : '',
        website_url: (config.branding?.support_url && config.branding.support_url.length > 0) ? config.branding.support_url[0] : '',
        success_url: config.redirect_urls?.success_url || '',
        cancel_url: config.redirect_urls?.cancel_url || '',
        webhook_url: (config.redirect_urls?.webhook_url && config.redirect_urls.webhook_url.length > 0) ? config.redirect_urls.webhook_url[0] : '',
        theme: {
          primary_color: config.theme?.primary_color || '#3B82F6',
          background_color: config.theme?.background_color || '#FFFFFF',
          text_color: config.theme?.text_color || '#1F2937',
          border_radius: config.theme?.border_radius || 8,
          font_family: config.theme?.font_family || 'Inter',
          button_style: config.theme?.button_style || 'solid',
        },
        allowed_tokens: config.payment_options?.allowed_tokens || ['ICP'],
        minimum_amount: config.minimum_amount,
        maximum_amount: config.maximum_amount,
        custom_fields: config.custom_fields || [],
        is_active: config.is_active ?? true,
        created_at: config.created_at || new Date().toISOString(),
        updated_at: config.updated_at || new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Failed to list modals:', error);
      return [];
    }
  }

  async get_modal_config() {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      // First get the list of modals to get modal_id
      const modals = await this.actor.list_my_modals();
      if (!modals || modals.length === 0) {
        return { success: false, error: 'No modals found' };
      }

      // Get first modal's ID
      const firstModalId = modals[0].modal_id;
      
      // Now get the specific modal config using that ID
      const result = await this.actor.get_modal_config(firstModalId);
      if ('Ok' in result && result.Ok) {
        const config = result.Ok;
        return {
          success: true,
          data: {
            modal_id: config.modal_id || 'mock-id',
            name: config.name || '',
            description: (config.description && config.description.length > 0) ? config.description[0] : '',
            company_name: config.branding?.company_name || '',
            company_logo: (config.branding?.logo_url && config.branding.logo_url.length > 0) ? config.branding.logo_url[0] : '',
            website_url: (config.branding?.support_url && config.branding.support_url.length > 0) ? config.branding.support_url[0] : '',
            success_url: config.redirect_urls?.success_url || '',
            cancel_url: config.redirect_urls?.cancel_url || '',
            webhook_url: (config.redirect_urls?.webhook_url && config.redirect_urls.webhook_url.length > 0) ? config.redirect_urls.webhook_url[0] : '',
            theme: {
              primary_color: config.theme?.primary_color || '#3B82F6',
              background_color: config.theme?.background_color || '#FFFFFF',
              text_color: config.theme?.text_color || '#1F2937',
              border_radius: config.theme?.border_radius || 8,
              font_family: config.theme?.font_family || 'Inter',
              button_style: config.theme?.button_style || 'solid',
            },
            allowed_tokens: config.payment_options?.allowed_tokens || ['ICP'],
            minimum_amount: config.minimum_amount,
            maximum_amount: config.maximum_amount,
            custom_fields: config.custom_fields || [],
            is_active: config.is_active ?? true,
            created_at: config.created_at || new Date().toISOString(),
            updated_at: config.updated_at || new Date().toISOString(),
          }
        };
      } else {
        return { success: false, error: result.Err || 'Failed to get modal config' };
      }
    } catch (error) {
      console.error('Failed to get modal configuration:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async getActiveCoupons() {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.list_active_coupons();
      return result.map(coupon => ({
        coupon_id: coupon.coupon_id,
        code: coupon.code,
        coupon_type: coupon.coupon_type,
        description: coupon.description,
        minimum_amount: coupon.minimum_amount?.[0] ? Number(coupon.minimum_amount[0]) : null,
        applicable_tokens: coupon.applicable_tokens,
        usage_limit: coupon.usage_limit?.[0] ? Number(coupon.usage_limit[0]) : null,
        used_count: Number(coupon.used_count),
        expires_at: coupon.expires_at?.[0] ? Number(coupon.expires_at[0]) : null,
        is_active: coupon.is_active
      }));
    } catch (error) {
      console.error('Failed to get active coupons:', error);
      return [];
    }
  }

  async validateCoupon(couponCode, amount, tokenSymbol) {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.validate_and_use_coupon(
        couponCode.toUpperCase(),
        BigInt(Math.floor(amount * 1e8)), // Convert to smallest unit
        tokenSymbol
      );

      if ('Ok' in result) {
        const [couponId, discountAmount] = result.Ok;
        return {
          success: true,
          coupon_id: couponId,
          discount_amount: Number(discountAmount) / 1e8 // Convert back to decimal
        };
      } else {
        return {
          success: false,
          error: result.Err
        };
      }
    } catch (error) {
      console.error('Failed to validate coupon:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async getActiveSubscriptionPlans() {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.list_active_subscription_plans();
      return result.map(plan => ({
        plan_id: plan.plan_id,
        name: plan.name,
        description: plan.description,
        price: Number(plan.price) / 1e8, // Convert to decimal
        token: plan.token,
        billing_interval: plan.billing_interval,
        trial_period_days: plan.trial_period_days?.[0] ? Number(plan.trial_period_days[0]) : null,
        max_subscriptions: plan.max_subscriptions?.[0] ? Number(plan.max_subscriptions[0]) : null,
        features: plan.features,
        is_active: plan.is_active
      }));
    } catch (error) {
      console.error('Failed to get subscription plans:', error);
      return [];
    }
  }

  async processPaymentRequest(paymentRequest) {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      // Log the current identity being used
      console.log('🔑 ProcessPaymentRequest Identity Info:', {
        hasIdentity: !!this.identity,
        identityType: this.identity?.constructor?.name,
        canisterId: this.canisterId
      });

      // Log the payment request details with amount conversion
      const amountBigInt = BigInt(paymentRequest.amount);
      console.log('💰 ProcessPaymentRequest Details:', {
        invoice_id: paymentRequest.invoice_id,
        amount_original: paymentRequest.amount,
        amount_bigint: amountBigInt.toString(),
        amount_decimal: Number(amountBigInt) / 1e8,
        token_symbol: paymentRequest.token_symbol,
        coupon_code: paymentRequest.coupon_code,
        metadata: paymentRequest.metadata
      });

      // Check if we can get the current principal
      try {
        const currentPrincipal = await this.actor.whoami();
        console.log('🆔 Current Principal from canister:', currentPrincipal.toText());
      } catch (err) {
        console.warn('Could not get current principal:', err);
      }

      const requestPayload = {
        invoice_id: paymentRequest.invoice_id,
        amount: amountBigInt,
        token_symbol: paymentRequest.token_symbol,
        coupon_code: paymentRequest.coupon_code ? [paymentRequest.coupon_code] : [],
        metadata: paymentRequest.metadata || []
      };

      console.log('📤 Sending payment request to canister:', requestPayload);
      const result = await this.actor.process_payment_request(requestPayload);
      console.log('📥 Payment request response from canister:', result);

      if ('Ok' in result) {
        const paymentResult = result.Ok;
        const processedResult = { 
          success: true, 
          transaction_id: paymentResult.transaction_id,
          amount_paid: Number(paymentResult.amount_paid),
          discount_applied: Number(paymentResult.discount_applied),
          final_amount: Number(paymentResult.final_amount),
          block_index: paymentResult.block_index?.[0] ? Number(paymentResult.block_index[0]) : null,
          payment_method: paymentResult.payment_method
        };
        
        console.log('✅ Processed payment result:', {
          ...processedResult,
          amount_paid_decimal: processedResult.amount_paid / 1e8,
          final_amount_decimal: processedResult.final_amount / 1e8
        });
        
        return processedResult;
      } else {
        console.error('❌ Payment request failed with error:', result.Err);
        
        // Parse the error to provide more specific feedback
        let errorMessage = result.Err;
        if (typeof result.Err === 'string') {
          if (result.Err.includes('InsufficientFunds')) {
            errorMessage = 'Insufficient token balance for this transaction';
          } else if (result.Err.includes('Allowance')) {
            errorMessage = 'Token allowance insufficient or expired. Please try again.';
          } else if (result.Err.includes('Transfer failed')) {
            errorMessage = `Token transfer failed: ${result.Err}`;
          } else if (result.Err.includes('Invalid')) {
            errorMessage = `Invalid payment request: ${result.Err}`;
          }
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Process payment request failed:', error);
      throw error;
    }
  }

  async approveToken(tokenCanisterId, amount) {
    try {
      if (!this.identity) {
        throw new Error('Identity not available for token approval');
      }

      // First, look up the token in our supported tokens to get the correct fee
      let configuredFee = BigInt(0);
      try {
        const supportedTokens = await this.getSupportedTokens();
        const tokenConfig = supportedTokens.find(token => 
          token.canister_id === tokenCanisterId || token.canister === tokenCanisterId
        );
        
        if (tokenConfig && tokenConfig.fee !== undefined) {
          configuredFee = BigInt(tokenConfig.fee);
          console.log('Using configured token fee from canister config:', configuredFee.toString());
        }
      } catch (err) {
        console.warn('Could not get token fee from supported tokens, will try direct lookup:', err);
      }

      console.log('Creating ICRC2 actor for token approval:', {
        tokenCanisterId,
        amount: amount.toString(),
        configuredFee: configuredFee.toString(),
        userPaymentCanisterId: this.canisterId
      });

      // Create HTTP agent with identity
      const agent = new HttpAgent({
        identity: this.identity,
        host: 'https://ic0.app'
      });

      if (process.env.NODE_ENV !== 'production') {
        await agent.fetchRootKey();
      }

      // Simple ICRC2 interface for approval
      const tokenInterface = ({ IDL }) => {
        return IDL.Service({
          'icrc2_approve': IDL.Func([
            IDL.Record({
              'fee': IDL.Opt(IDL.Nat),
              'memo': IDL.Opt(IDL.Vec(IDL.Nat8)),
              'from_subaccount': IDL.Opt(IDL.Vec(IDL.Nat8)),
              'created_at_time': IDL.Opt(IDL.Nat64),
              'amount': IDL.Nat,
              'expected_allowance': IDL.Opt(IDL.Nat),
              'expires_at': IDL.Opt(IDL.Nat64),
              'spender': IDL.Record({
                'owner': IDL.Principal,
                'subaccount': IDL.Opt(IDL.Vec(IDL.Nat8))
              })
            })
          ], [
            IDL.Variant({
              'Ok': IDL.Nat,
              'Err': IDL.Variant({
                'GenericError': IDL.Record({ 'message': IDL.Text, 'error_code': IDL.Nat }),
                'TemporarilyUnavailable': IDL.Null,
                'Duplicate': IDL.Record({ 'duplicate_of': IDL.Nat }),
                'BadFee': IDL.Record({ 'expected_fee': IDL.Nat }),
                'AllowanceChanged': IDL.Record({ 'current_allowance': IDL.Nat }),
                'CreatedInFuture': IDL.Record({ 'ledger_time': IDL.Nat64 }),
                'TooOld': IDL.Null,
                'Expired': IDL.Record({ 'ledger_time': IDL.Nat64 }),
                'InsufficientFunds': IDL.Record({ 'balance': IDL.Nat })
              })
            })
          ], []),
          'icrc1_fee': IDL.Func([], [IDL.Nat], ['query'])
        });
      };

      const tokenActor = Actor.createActor(tokenInterface, {
        agent,
        canisterId: Principal.fromText(tokenCanisterId)
      });

      // If we don't have a configured fee yet, try to get it directly from the token canister
      if (configuredFee === BigInt(0)) {
        try {
          const tokenFee = await tokenActor.icrc1_fee();
          console.log('Token fee fetched directly from token canister:', Number(tokenFee));
          configuredFee = tokenFee;
        } catch (err) {
          console.warn('Could not get token fee from token canister, using 0:', err);
          // Keep configuredFee as 0
        }
      }

      // Prepare approval arguments
      const approveArgs = {
        fee: [],
        memo: [],
        from_subaccount: [],
        created_at_time: [],
        amount: amount,
        expected_allowance: [],
        expires_at: [],
        spender: { 
          owner: Principal.fromText(this.canisterId), 
          subaccount: [] 
        }
      };

      console.log('Approving token with args:', approveArgs);
      const approveResult = await tokenActor.icrc2_approve(approveArgs);
      console.log('Token approval result:', approveResult);

      if ('Err' in approveResult) {
        throw new Error(`Token approval failed: ${JSON.stringify(approveResult.Err)}`);
      }

      return {
        success: true,
        block_index: Number(approveResult.Ok),
        amount_approved: amount.toString(),
        token_canister: tokenCanisterId,
        fee_used: configuredFee.toString()
      };
    } catch (error) {
      console.error('Token approval failed:', error);
      throw error;
    }
  }

  async transferToken(tokenCanister, amount, recipient) {
    try {
      if (!this.actor) {
        await this.initializeActor();
      }

      const result = await this.actor.transfer_token({
        token_canister: Principal.fromText(tokenCanister),
        amount: BigInt(amount),
        to: Principal.fromText(recipient)
      });

      if ('Ok' in result) {
        return { success: true, transactionId: result.Ok };
      } else {
        throw new Error(result.Err);
      }
    } catch (error) {
      console.error('Transfer failed:', error);
      throw error;
    }
  }
}

export default UserPaymentService;
